import { useCallback, useEffect, useRef } from "react";
import { isNetworkError, type Exercise } from "@heraion/shared";
import {
  useInboxContext,
  useMaxBumpOffer,
  type DurableWriteFn,
  type MaxBumpInput,
} from "@heraion/ui";

import { durableWrite } from "../durableWrite";
import { useExerciseCatalog } from "../logger/useExerciseCatalog";
import { useMaxesDataWeb } from "./useMaxesDataWeb";

// web-rnw twin of apps/mobile/src/screens/useMaxBumpFlow.ts. The offer itself
// is the shared useMaxBumpOffer (#182); the answer lives in the bell. Only
// the mechanics differ: web's durableWrite has no queue, and the MAXes come
// from useMaxesDataWeb (web has no MaxesContext singleton).
// It also hands WorkoutsScreen the catalog, for its names and edit hydration.
export function useMaxBumpFlowWeb(): {
  catalog: Exercise[] | null;
  evaluateAfterSave: (maxBumpInput?: MaxBumpInput) => readonly string[];
} {
  const { maxes, maxMeta, loaded } = useMaxesDataWeb();
  const { refresh } = useInboxContext();
  // An offer whose POST met no connection is sent once more when the browser
  // is back online. A side effect, not a queue: the write still answers
  // `rejected` (web never answers `queued`), and it lives only in this open
  // tab, until it fires or the screen unmounts. A second send carries a new
  // Idempotency-Key, which is harmless: the server rewrites the row of the
  // same session (sessionStartedAt <= :s, apps/api/src/lib/maxBumpWrites.ts
  // writeMaxBumpRow).
  const resends = useRef(new Set<() => void>());
  useEffect(() => {
    const pending = resends.current;
    return () => {
      for (const fire of pending) window.removeEventListener("online", fire);
      pending.clear();
    };
  }, []);
  const offerWrite: DurableWriteFn = useCallback(
    async (op) => {
      const result = await durableWrite(op);
      if (result.outcome === "sent") void refresh();
      else if (
        result.outcome === "rejected" &&
        isNetworkError(result.error) &&
        typeof window !== "undefined"
      ) {
        const body = op.body;
        const fire = () => {
          resends.current.delete(fire);
          void durableWrite({ method: "POST", path: op.path, body }).then((again) => {
            if (again.outcome === "sent") void refresh();
          });
        };
        resends.current.add(fire);
        window.addEventListener("online", fire, { once: true });
      }
      return result;
    },
    [refresh],
  );
  const evaluateAfterSave = useMaxBumpOffer({
    maxes,
    maxMeta,
    loaded,
    durableWrite: offerWrite,
  });
  // Best-effort catalog hydration, for names and edit hydration.
  const { catalog } = useExerciseCatalog();

  return { catalog, evaluateAfterSave };
}
